// React Query hooks per BE-029 endpoint — FE-031.
//
// Each hook is a thin wrapper around the corresponding ``api.ts``
// function and pins a stable query-key shape so the FE-032..FE-037
// chunks can rely on cache invalidation behavior. ``staleTime`` is
// 30 seconds (the queryClient default in main.tsx); per-hook
// overrides land in the relevant widget chunk if needed.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  getCfd,
  getCfdBottleneck,
  getClientAnalytics,
  getClientPerformance,
  getEmployeeAnalytics,
  getEmployeePerformance,
  getBlockingTasks,
  getEmployeeDelivery,
  getGoingCold,
  getHealthKpis,
  getTaskCompletionTrend,
  getTouchpointHealth,
  getMostOverdue,
  getRenewalPipeline,
  getRiskWorkload,
} from "./api";
import type {
  CfdBottleneckResponse,
  CfdParams,
  CfdResponse,
  ClientAnalyticsResponse,
  ClientPerformanceResponse,
  DashboardCommonFilters,
  DashboardTableQuery,
  EmployeeAnalyticsResponse,
  EmployeePerformanceResponse,
  GoingColdParams,
  BlockingTasksResponse,
  EmployeeDeliveryResponse,
  GoingColdResponse,
  HealthKpisResponse,
  TaskCompletionTrendResponse,
  TouchpointHealthResponse,
  MostOverdueResponse,
  RenewalPipelineParams,
  RenewalPipelineResponse,
  RiskWorkloadResponse,
} from "./types";

// Query-key prefix shared by every hook so a single
// `queryClient.invalidateQueries(['dashboard'])` busts the whole
// dashboard cache.
const DASHBOARD_KEY = "dashboard" as const;

interface HookOptions {
  enabled?: boolean;
}

export function useEmployeeAnalyticsQuery(
  params?: DashboardCommonFilters,
  opts?: HookOptions,
): UseQueryResult<EmployeeAnalyticsResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "employee_analytics", params ?? null],
    queryFn: ({ signal }) => getEmployeeAnalytics(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useMostOverdueQuery(
  params?: DashboardCommonFilters,
  opts?: HookOptions,
): UseQueryResult<MostOverdueResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "most_overdue", params ?? null],
    queryFn: ({ signal }) => getMostOverdue(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useEmployeePerformanceQuery(
  params?: DashboardTableQuery,
  opts?: HookOptions,
): UseQueryResult<EmployeePerformanceResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "employee_performance", params ?? null],
    queryFn: ({ signal }) => getEmployeePerformance(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useClientAnalyticsQuery(
  params?: DashboardCommonFilters,
  opts?: HookOptions,
): UseQueryResult<ClientAnalyticsResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "client_analytics", params ?? null],
    queryFn: ({ signal }) => getClientAnalytics(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useRenewalPipelineQuery(
  params?: RenewalPipelineParams,
  opts?: HookOptions,
): UseQueryResult<RenewalPipelineResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "renewal_pipeline", params ?? null],
    queryFn: ({ signal }) => getRenewalPipeline(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useClientPerformanceQuery(
  params?: DashboardTableQuery,
  opts?: HookOptions,
): UseQueryResult<ClientPerformanceResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "client_performance", params ?? null],
    queryFn: ({ signal }) => getClientPerformance(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useGoingColdQuery(
  params?: GoingColdParams,
  opts?: HookOptions,
): UseQueryResult<GoingColdResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "going_cold", params ?? null],
    queryFn: ({ signal }) => getGoingCold(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useTouchpointHealthQuery(
  params?: { limit?: number },
  opts?: HookOptions,
): UseQueryResult<TouchpointHealthResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "touchpoint_health", params?.limit ?? null],
    queryFn: ({ signal }) => getTouchpointHealth(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}


export function useHealthKpisQuery(
  params?: DashboardCommonFilters,
  opts?: HookOptions,
): UseQueryResult<HealthKpisResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "health_kpis", params ?? null],
    queryFn: ({ signal }) => getHealthKpis(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}


export function useBlockingTasksQuery(
  params?: DashboardCommonFilters,
  opts?: HookOptions,
): UseQueryResult<BlockingTasksResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "blocking_tasks", params ?? null],
    queryFn: ({ signal }) => getBlockingTasks(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}


export function useEmployeeDeliveryQuery(
  params?: DashboardCommonFilters,
  opts?: HookOptions,
): UseQueryResult<EmployeeDeliveryResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "employee_delivery", params ?? null],
    queryFn: ({ signal }) => getEmployeeDelivery(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}


export function useTaskCompletionTrendQuery(
  params?: { months?: number },
  opts?: HookOptions,
): UseQueryResult<TaskCompletionTrendResponse> {
  return useQuery({
    queryKey: [
      DASHBOARD_KEY, "task_completion_trend", params?.months ?? null,
    ],
    queryFn: ({ signal }) => getTaskCompletionTrend(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}


export function useRiskWorkloadQuery(
  params?: DashboardCommonFilters,
  opts?: HookOptions,
): UseQueryResult<RiskWorkloadResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "risk_workload", params ?? null],
    queryFn: ({ signal }) => getRiskWorkload(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useCfdQuery(
  params?: CfdParams,
  opts?: HookOptions,
): UseQueryResult<CfdResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "cfd", params ?? null],
    queryFn: ({ signal }) => getCfd(params, { signal }),
    enabled: opts?.enabled ?? true,
  });
}

export function useCfdBottleneckQuery(
  opts?: HookOptions,
): UseQueryResult<CfdBottleneckResponse> {
  return useQuery({
    queryKey: [DASHBOARD_KEY, "cfd_bottleneck"],
    queryFn: ({ signal }) => getCfdBottleneck({ signal }),
    enabled: opts?.enabled ?? true,
  });
}

export const DASHBOARD_QUERY_KEY = DASHBOARD_KEY;
